import React from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Alert } from 'react-bootstrap';

const Disclaimer: React.FC = () => {
  const isMobile = useIsMobile();

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body className="p-4 p-md-5">
          <h1 className="mb-4">Disclaimer</h1>

          <div className="text-muted mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </div>

          <Alert variant="warning" className="mb-4">
            <p className="fw-bold mb-2">
              ⚠️ Important Notice
            </p>
            <p className="mb-0 small">
              Please read this disclaimer carefully before using NayaBajar. By using our platform,
              you agree to these terms and understand your rights and responsibilities.
            </p>
          </Alert>

          <section className="mb-5">
            <h2 className="mb-3">1. Nature of Service</h2>
            <p className="mb-3">
              NayaBajar is an online classifieds platform that facilitates connections between buyers
              and sellers. We act as an intermediary and are NOT:
            </p>
            <ul>
              <li>A party to any transaction between users</li>
              <li>An agent, broker, or representative for any user</li>
              <li>A guarantor or insurer of any transaction</li>
              <li>A provider of escrow or payment processing services</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">2. No Warranty</h2>
            <p className="mb-3">
              NayaBajar is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind,
              either express or implied, including but not limited to:
            </p>
            <ul>
              <li>MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY, RELIABILITY, OR AVAILABILITY</li>
              <li>UNINTERRUPTED OR ERROR-FREE OPERATION</li>
              <li>SECURITY OF DATA OR INFORMATION</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">3. User Responsibilities</h2>

            <h3 className="h5 mb-3">3.1 As a Buyer</h3>
            <p className="mb-3">You are solely responsible for:</p>
            <ul className="mb-4">
              <li>Verifying the authenticity and condition of items</li>
              <li>Confirming the identity and credibility of sellers</li>
              <li>Conducting due diligence before making payments</li>
              <li>Understanding the terms of sale and delivery</li>
              <li>Ensuring the transaction complies with applicable laws</li>
            </ul>

            <h3 className="h5 mb-3">3.2 As a Seller</h3>
            <p className="mb-3">You are solely responsible for:</p>
            <ul className="mb-4">
              <li>Accuracy and truthfulness of your listings</li>
              <li>Legal ownership of items being sold</li>
              <li>Compliance with all applicable laws and regulations</li>
              <li>Honoring commitments and delivery promises</li>
              <li>Protecting buyer information and privacy</li>
            </ul>

            <h3 className="h5 mb-3">3.3 Safety Guidelines</h3>
            <Alert variant="danger" className="border">
              <p className="fw-bold mb-2">⚠️ Safety Reminders</p>
              <ul className="mb-0 small">
                <li>Meet in safe, public locations with other people present</li>
                <li>Inspect items thoroughly before making payment</li>
                <li>Never pay in advance without seeing the item</li>
                <li>Be cautious of deals that seem too good to be true</li>
                <li>Trust your instincts – if something feels wrong, walk away</li>
                <li>Keep records of all communications and transactions</li>
              </ul>
            </Alert>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">4. Content Accuracy</h2>
            <p className="mb-3">
              While we strive to maintain accurate and up-to-date information, we make no representations
              or warranties of any kind, express or implied, about the:
            </p>
            <ul>
              <li>Completeness, accuracy, reliability, or timeliness of content</li>
              <li>Quality, safety, or legality of listed items</li>
              <li>Qualifications or credibility of sellers</li>
              <li>Truthfulness of user reviews or ratings</li>
            </ul>
            <p className="mt-3">
              Any reliance on such content is at your own risk.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">5. Prohibited Activities</h2>
            <p className="mb-3">
              The following activities are strictly prohibited on our platform:
            </p>
            <ul>
              <li>Selling illegal, stolen, or counterfeit goods</li>
              <li>Fraudulent or deceptive practices</li>
              <li>Money laundering or illegal financial activities</li>
              <li>Scams, phishing, or identity theft</li>
              <li>Hate speech, discrimination, or harassment</li>
              <li>Spam, unsolicited communications, or automated scraping</li>
              <li>Interfering with Service operation or security</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">6. Third-Party Links & Services</h2>
            <p className="mb-3">
              Our Service may contain links to third-party websites or services. We:
            </p>
            <ul>
              <li>Do not endorse, guarantee, or take responsibility for external sites</li>
              <li>Are not responsible for the content or practices of third parties</li>
              <li>Do not make any representations regarding the accuracy of third-party content</li>
            </ul>
            <p className="mt-3">
              Your interaction with third-party sites is governed by their terms and policies.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">7. Financial Transactions</h2>
            <p className="mb-3">
              NayaBajar does NOT:
            </p>
            <ul className="mb-3">
              <li>Process payments or provide escrow services</li>
              <li>Offer buyer protection or insurance programs</li>
              <li>Guarantee refunds or resolve payment disputes</li>
              <li>Verify the legitimacy of payment methods</li>
            </ul>
            <Alert variant="warning" className="border mt-3">
              <p className="fw-bold mb-0">
                💰 Payment Safety Warning
              </p>
              <p className="small mt-2 mb-0">
                Never make advance payments. Use secure payment methods and verify
                items before paying. If a deal seems suspicious, report it immediately.
              </p>
            </Alert>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">8. Limitation of Liability</h2>
            <p className="mb-3">
              To the fullest extent permitted by law, NayaBajar shall not be liable for:
            </p>
            <ul>
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, or business opportunities</li>
              <li>Personal injury or property damage</li>
              <li>Fraudulent or deceptive activities by other users</li>
              <li>Transactions that go wrong or result in disputes</li>
              <li>Any losses exceeding the amount you paid (if any) for the Service</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">9. Indemnification</h2>
            <p className="mb-3">
              You agree to indemnify and hold harmless NayaBajar, its officers, directors,
              employees, and agents from any claims, damages, losses, liabilities, and expenses
              arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Transactions or interactions with other users</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">10. No Professional Advice</h2>
            <p className="mb-3">
              Content on NayaBajar is for general informational purposes only and does NOT constitute:
            </p>
            <ul>
              <li>Legal, financial, or professional advice</li>
              <li>Recommendations or endorsements</li>
              <li>Valuation or appraisal services</li>
            </ul>
            <p className="mt-3">
              Always seek professional advice for specific situations.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">11. Dispute Resolution</h2>
            <p className="mb-3">
              NayaBajar is not obligated to resolve disputes between users. However, we may:
            </p>
            <ul>
              <li>Provide tools for users to resolve disputes amicably</li>
              <li>Suspend or ban users who violate our policies</li>
              <li>Cooperate with law enforcement when appropriate</li>
              <li>Remove fraudulent or illegal listings</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">12. Acknowledgment</h2>
            <p className="mb-3">
              By using NayaBajar, you acknowledge that you have read, understood, and agree to:
            </p>
            <ul>
              <li>This Disclaimer and our Terms of Service</li>
              <li>Privacy Policy and Cookie Policy</li>
              <li>The inherent risks of online transactions</li>
              <li>Your sole responsibility for using the Service</li>
            </ul>
            <p className="mt-3 fw-bold">
              If you do not agree with any part of this disclaimer, please do not use our Service.
            </p>
          </section>

          {/* Contact Section */}
          <div className="mt-4 pt-4 border-top">
            <h2 className="mb-3">Contact & Support</h2>
            <p className="mb-2">
              For questions, concerns, or to report suspicious activity:
            </p>
            <ul className="list-unstyled">
              <li><strong>Email:</strong>{' '}
                <a href="mailto:support@nayabajar.com" className="text-primary">
                  support@nayabajar.com
                </a>
              </li>
              <li><strong>Report Issue:</strong>{' '}
                <a href="/contact" className="text-primary">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );

  if (isMobile) {
    return <MobileLayout>{content}</MobileLayout>;
  }

  return <Layout showSidebar={false}>{content}</Layout>;
};

export default Disclaimer;
